import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Send, Cpu, Plus, Loader2, MessageCircle, User } from "lucide-react";
import ProGate from "@/components/ProGate";
import GuestGate from "@/components/GuestGate";
import { AiPackGate, useAiPackReturn } from "@/components/AiPackGate";

const AI_AVATAR = "https://static.prod-images.emergentagent.com/jobs/51c27263-25dc-4f0a-a858-db6e64a15193/images/cdfb578a7b0e0dca623364a23a9aa048355117b92d5c1a360f0b6a1e357945cc.png";

const GUEST_REPLY = {
  sk: "Ahoj ty zvedavec! 👋 Keď sa zaregistruješ alebo si dáš predplatné, neboj sa — pomôžem ti so všetkým a bude kopec srandy. 🤝",
  en: "Hey there, curious one! 👋 Once you register or subscribe, don't worry — I'll help you with everything and we'll have a blast. 🤝",
  cs: "Ahoj ty zvědavče! 👋 Až se zaregistruješ nebo si dáš předplatné, neboj — pomůžu ti se vším a bude kopec srandy. 🤝",
  de: "Hallo du Neugieriger! 👋 Sobald du dich registrierst oder ein Abo abschließt, keine Sorge — ich helfe dir bei allem und es wird ein Riesenspaß. 🤝",
  pl: "Cześć ciekawski! 👋 Gdy się zarejestrujesz albo wykupisz subskrypcję, nie martw się — pomogę ci ze wszystkim i będzie kopa śmiechu. 🤝",
};

export default function AIAdvisor() {
  const { user, loading: authLoading } = useAuth();
  const { lang, t } = useLang();
  useAiPackReturn(lang);
  const isGuest = !user;
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    loadSessions();
  }, [user]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Use rAF + direct scrollTop assignment — more reliable on iOS Safari
    // than scrollTo({behavior: 'smooth'}) which can fail when content is still rendering
    const tick = () => { el.scrollTop = el.scrollHeight; };
    tick();
    const id = requestAnimationFrame(tick);
    const timer = setTimeout(tick, 120);
    return () => { cancelAnimationFrame(id); clearTimeout(timer); };
  }, [messages, sending]);

  const loadSessions = async () => {
    const r = await api.get("/ai/sessions");
    setSessions(r.data);
  };

  const openSession = async (sid) => {
    setSessionId(sid);
    const r = await api.get(`/ai/history/${sid}`);
    setMessages(r.data);
    setMobileHistoryOpen(false);
  };

  const startNew = () => { setSessionId(null); setMessages([]); setMobileHistoryOpen(false); };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const text = input;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, id: "tmp-u" + Date.now() }]);

    // Guest mode: always reply with the fixed invitation message — never hits the API
    if (isGuest) {
      setSending(true);
      await new Promise((r) => setTimeout(r, 600)); // small UX delay
      setMessages((m) => [...m, { role: "assistant", content: GUEST_REPLY[lang] || GUEST_REPLY.sk, id: "tmp-g" + Date.now() }]);
      setSending(false);
      return;
    }

    setSending(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sessionId, message: text, lang });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, id: "tmp-a" }]);
      loadSessions();
    } catch (err) {
      const detail = err.response?.data?.detail || "AI error";
      const isProGate = detail.startsWith("PRO_REQUIRED:");
      const isPackGate = detail.startsWith("AI_PACK_REQUIRED:");
      setMessages((m) => [...m, {
        role: "assistant",
        content: isProGate || isPackGate ? detail : "⚠ " + detail,
        id: "tmp-e",
        proGate: isProGate,
        packGate: isPackGate,
      }]);
    } finally { setSending(false); }
  };

  if (authLoading) return null;

  return (
    <div data-testid="ai-advisor-page" className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-12 gap-4 h-[calc(100dvh-8rem)]">
      {/* Sidebar (desktop only) */}
      <aside className="hidden lg:flex lg:col-span-3 border border-zinc-800 bg-[var(--wb-surface)] flex-col" data-testid="ai-sidebar">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <span className="font-display font-bold uppercase tracking-wider text-sm">{t.ai.history}</span>
          <button
            data-testid="new-chat-btn"
            onClick={startNew}
            className="wb-btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1"
          ><Plus className="w-3 h-3" /> {t.ai.newChat}</button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <div className="text-xs text-zinc-600 font-mono uppercase p-3">{t.ai.empty}</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.id}
              data-testid={`session-${s.id}`}
              onClick={() => openSession(s.id)}
              className={`w-full text-left p-2 text-sm truncate border-l-2 transition-colors ${sessionId === s.id ? "border-gold-500 bg-zinc-900/50 text-white" : "border-transparent text-zinc-400 hover:text-white hover:border-zinc-700"}`}
            >
              <MessageCircle className="w-3 h-3 inline mr-1 text-gold-500" />
              {s.title || "Conversation"}
            </button>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <section className="lg:col-span-9 border border-zinc-800 bg-[var(--wb-surface)] flex flex-col h-full min-h-0 overflow-hidden" data-testid="ai-chat-section">
        {isGuest && (
          <div className="p-3 border-b border-zinc-800">
            <GuestGate compact />
          </div>
        )}
        <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
          <img src={AI_AVATAR} alt="AI" className="w-10 h-10 border border-gold-600" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg">{t.ai.title}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
              Claude Sonnet 4.5 · <span className="text-green-500">●</span> {t.common.ready}
            </div>
          </div>
          {/* Mobile-only history button */}
          <button
            data-testid="mobile-history-btn"
            onClick={() => setMobileHistoryOpen((v) => !v)}
            className="lg:hidden wb-btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1"
            aria-label={t.ai.history}
          >
            <MessageCircle className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile history dropdown */}
        {mobileHistoryOpen && (
          <div className="lg:hidden border-b border-zinc-800 bg-zinc-950 max-h-60 overflow-y-auto" data-testid="mobile-history-panel">
            <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
              <span className="text-xs font-display font-bold uppercase tracking-wider">{t.ai.history}</span>
              <button
                data-testid="mobile-new-chat-btn"
                onClick={startNew}
                className="wb-btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1"
              ><Plus className="w-3 h-3" /> {t.ai.newChat}</button>
            </div>
            <div className="p-2 space-y-1">
              {sessions.length === 0 && (
                <div className="text-xs text-zinc-600 font-mono uppercase p-2">{t.ai.empty}</div>
              )}
              {sessions.map((s) => (
                <button
                  key={s.id}
                  data-testid={`mobile-session-${s.id}`}
                  onClick={() => openSession(s.id)}
                  className={`w-full text-left p-2 text-sm truncate border-l-2 transition-colors ${sessionId === s.id ? "border-gold-500 bg-zinc-900/50 text-white" : "border-transparent text-zinc-400 hover:text-white hover:border-zinc-700"}`}
                >
                  <MessageCircle className="w-3 h-3 inline mr-1 text-gold-500" />
                  {s.title || "Conversation"}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="text-center py-12 text-zinc-600">
              <Cpu className="w-10 h-10 mx-auto mb-3 text-gold-500" strokeWidth={1.2} />
              <p className="text-sm font-mono uppercase tracking-wider">{t.ai.empty}</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
                {(t.ai.suggestions || []).map((p, i) => (
                  <button key={i} onClick={() => setInput(p)}
                    data-testid={`prompt-suggest-${i}`}
                    className="text-left text-xs p-3 border border-zinc-800 hover:border-gold-600 text-zinc-400 hover:text-white transition-colors">
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id || i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && !m.proGate && !m.packGate && (
                <img src={AI_AVATAR} alt="" className="w-8 h-8 border border-gold-600 flex-shrink-0" />
              )}
              {m.packGate ? (
                <div className="w-full max-w-[80%]"><AiPackGate lang={lang} message={m.content.replace("AI_PACK_REQUIRED:", "").trim()} /></div>
              ) : m.proGate ? (
                <div className="w-full max-w-[80%]"><ProGate compact message={m.content.replace("PRO_REQUIRED:", "").trim()} /></div>
              ) : (
                <div className={`max-w-[80%] ${m.role === "user" ? "" : "space-y-1"}`}>
                  <div className={`p-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-gold-600/10 border border-gold-700 text-white" : "bg-zinc-900/50 border border-zinc-800 text-zinc-200"}`}>
                    {m.content}
                  </div>
                  {m.role === "assistant" && (
                    <div className="text-[10px] text-zinc-600 italic leading-relaxed pl-1 font-mono">
                      ⚠ {{
                        sk: "AI rada je informačná. Vždy konzultuj s certifikovaným technológom a over skúšobným zvarom.",
                        en: "AI advice is informational. Always consult a certified welding engineer and validate with a test weld.",
                        cs: "Rada AI je informační. Vždy konzultuj s certifikovaným technologem a ověř zkušebním svarem.",
                        de: "KI-Rat ist nur informativ. Konsultieren Sie immer einen zertifizierten Schweißingenieur und validieren Sie mit einer Probeschweißung.",
                        pl: "Porada AI ma charakter informacyjny. Zawsze konsultuj z certyfikowanym technologiem i zweryfikuj próbną spoiną.",
                      }[lang] || "AI rada je informačná. Vždy konzultuj s certifikovaným technológom a over skúšobným zvarom."}
                    </div>
                  )}
                </div>
              )}
              {m.role === "user" && (
                <div className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-zinc-400" />
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex gap-3 items-center">
              <img src={AI_AVATAR} alt="" className="w-8 h-8 border border-gold-600" />
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> {t.ai.thinking}
              </div>
            </div>
          )}
        </div>

        <form onSubmit={send} className="p-3 border-t border-zinc-800 flex gap-2 bg-[var(--wb-surface)] pb-[max(env(safe-area-inset-bottom),12px)]" data-testid="ai-form">
          <input
            data-testid="ai-input"
            value={input} onChange={(e) => setInput(e.target.value)}
            placeholder={t.ai.placeholder}
            className="wb-input flex-1 px-3 py-2 text-sm"
          />
          <button
            data-testid="ai-send-btn"
            type="submit" disabled={sending || !input.trim()}
            className="wb-btn-primary px-4 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> {t.ai.send}
          </button>
        </form>
      </section>
    </div>
  );
}
